// API service for communicating with the Wordle solver backend

import { API_CONFIG } from '../config';
import { WordleApiParams, WordleApiResponse } from '../types';

export class WordleApiService {
  private baseUrl: string;
  private authToken?: string;

  constructor(baseUrl?: string, authToken?: string) {
    this.baseUrl = baseUrl || API_CONFIG.BASE_URL;
    this.authToken = authToken;
  }

  /**
   * Fetch possible words from the Wordle solver API
   */
  async getSolutions(params: WordleApiParams): Promise<WordleApiResponse> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (params.with && params.with.length > 0) {
        queryParams.append('with', params.with);
      }

      if (params.without && params.without.length > 0) {
        queryParams.append('without', params.without);
      }

      if (params.known && params.known.length > 0) {
        queryParams.append('known', params.known);
      }

      if (params.knownnot && params.knownnot.length > 0) {
        params.knownnot.forEach(knownNot => {
          if (knownNot.length > 0) {
            queryParams.append('knownnot', knownNot);
          }
        });
      }

      const url = `${this.baseUrl}/suggest?${queryParams.toString()}`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      // Add authorization header if token is available
      if (this.authToken) {
        headers['Authorization'] = `Bearer ${this.authToken}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(`API access forbidden (403). This may be a CORS issue. Please ensure your API allows requests from ${window.location.origin}`);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle both response formats:
      // External API: { words: [...], count: ... }
      // Internal API: "word1 word2 word3 ..."
      if (typeof data === 'string') {
        // Internal API returns space-separated words
        const words = data.trim().split(/\s+/).filter(w => w.length > 0);
        return {
          words,
          count: words.length
        };
      }
      
      return data as WordleApiResponse;
    } catch (error) {
      console.error('Error fetching Wordle solutions:', error);

      // Provide more specific error messages for common CORS issues
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error(`Network error: Unable to reach the API. This may be due to CORS restrictions. Please ensure your API server at ${this.baseUrl} allows requests from ${window.location.origin}`);
      }

      throw new Error(`Failed to fetch solutions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}